// ---------------------------------------------------------------------------
// O layout que descreve a comanda de HOJE.
//
// Não é um layout novo: é a tradução, para dado, do que printer.service.ts já
// manda para a impressora. Serve de ponto de partida no editor da Central e de
// garantia de que ligar o layout em dado não muda nada na cozinha.
//
// Toda esquisitice do código legado está reproduzida de propósito e comentada:
// "Número do Pessoas" com o erro de concordância, cabeçalho de coluna com
// largura 0.15 e célula com 0.10, o "|" grudado no título "Produto", a linha
// de comanda que não sai em negrito. Mudar isso é decisão do Guilherme, não do
// código — e agora dá para mudar sem tocar em programa.
// ---------------------------------------------------------------------------

import {
  Alinhamento,
  CampoComanda,
  ChaveCampo,
  CondicaoCampo,
  LayoutComanda,
} from "./tipos";

interface OpcoesCampo {
  rotulo?: string | null;
  texto?: string;
  repeticoes?: number;
  alinhamento?: Alinhamento;
  negrito?: boolean;
  alturaDupla?: boolean;
  larguraDupla?: boolean;
  maiusculas?: boolean;
  linhasEmBrancoDepois?: number;
  condicao?: CondicaoCampo;
  chaveCondicao?: ChaveCampo;
  sufixo?: string;
  ativo?: boolean;
}

function campo(chave: ChaveCampo, opcoes: OpcoesCampo = {}): CampoComanda {
  return {
    chave,
    rotulo: opcoes.rotulo ?? null,
    texto: opcoes.texto,
    repeticoes: opcoes.repeticoes,
    ativo: opcoes.ativo ?? true,
    alinhamento: opcoes.alinhamento ?? "esquerda",
    negrito: opcoes.negrito ?? false,
    alturaDupla: opcoes.alturaDupla ?? false,
    larguraDupla: opcoes.larguraDupla ?? false,
    maiusculas: opcoes.maiusculas,
    linhasEmBrancoDepois: opcoes.linhasEmBrancoDepois,
    condicao: opcoes.condicao,
    chaveCondicao: opcoes.chaveCondicao,
    sufixo: opcoes.sufixo,
  };
}

const branco = (alinhamento: Alinhamento, condicao?: CondicaoCampo) =>
  campo("linhaEmBranco", { alinhamento, condicao });

/** A régua "_" x 41 que o código de hoje usa em todo canto. */
const regua = (alinhamento: Alinhamento, opcoes: OpcoesCampo = {}) =>
  campo("separador", {
    texto: "_",
    repeticoes: 41,
    alinhamento,
    ...opcoes,
  });

// ---------------------------------------------------------------------------
// VIA DE PRODUÇÃO — a comanda da cozinha (printProductOrderProduction)
// Largura 56 colunas, como está no código.
// ---------------------------------------------------------------------------

export function layoutPadraoProducao(): LayoutComanda {
  return {
    versao: 1,
    nome: "Via de produção (padrão do sistema)",
    documento: "producao",
    larguraColunas: 56,
    larguraPapelMm: 80,
    blocos: [
      {
        tipo: "cabecalho",
        titulo: "Cabeçalho",
        ativo: true,
        campos: [
          campo("filial", { alinhamento: "centro", negrito: true }),
          branco("centro"),
          campo("codigoPedido", {
            rotulo: "Número do Pedido",
            alinhamento: "centro",
            condicao: "mesaOuComanda",
          }),
          branco("centro"),
          campo("textoFixo", {
            texto: "VIA DE PRODUÇÃO",
            alinhamento: "centro",
          }),
          branco("centro"),
        ],
      },
      {
        tipo: "dadosPedido",
        titulo: "Dados do pedido",
        ativo: true,
        campos: [
          campo("data", { rotulo: "Data" }),
          campo("periodo", { rotulo: "Período" }),
          campo("caixa", { rotulo: "Caixa" }),
          campo("operador", { rotulo: "Operador" }),
          // Sim, "Número do Pessoas". É o texto que sai hoje.
          campo("numeroPessoas", {
            rotulo: "Número do Pessoas",
            condicao: "mesa",
          }),
          branco("esquerda"),
          // ACHADO: o código chama setTextDoubleHeight() e logo depois
          // setTextDoubleWidth(). Os dois são "ESC ! n" e o segundo APAGA o
          // primeiro — então o que sai na bobina hoje é largura dupla e altura
          // NORMAL, não "letra grande" nos dois eixos. Reproduzido como está.
          campo("identificacaoItem", {
            alinhamento: "centro",
            alturaDupla: false,
            larguraDupla: true,
          }),
          branco("centro"),
          // No delivery estes campos saem CENTRALIZADOS na via de produção
          // (o código não volta para alignLeft nesse ramo).
          campo("clienteNome", {
            rotulo: "CLIENTE",
            alinhamento: "centro",
            maiusculas: true,
            condicao: "temValor",
          }),
          campo("clienteTelefone", {
            rotulo: "TELEFONE",
            alinhamento: "centro",
            condicao: "temValor",
          }),
          campo("clienteEndereco", {
            rotulo: "ENDEREÇO",
            alinhamento: "centro",
            maiusculas: true,
            condicao: "temValor",
          }),
          campo("clienteComplemento", {
            rotulo: "COMPLEMENTO",
            alinhamento: "centro",
            maiusculas: true,
            condicao: "temValor",
          }),
          campo("clienteObservacao", {
            rotulo: "OBSERVAÇÃO",
            alinhamento: "centro",
            maiusculas: true,
            condicao: "temValor",
          }),
          branco("centro", "delivery"),
        ],
      },
      {
        tipo: "itens",
        titulo: "Itens — mesa",
        ativo: true,
        condicao: "mesa",
        campos: [],
        alinhamentoTabela: "centro",
        negritoCabecalho: false,
        negritoItem: true,
        ordenarPorLugar: true,
        separadorAntesDoCabecalho: regua("centro"),
        separadorDepoisDoCabecalho: regua("centro"),
        separadorEntreItens: regua("centro"),
        colunas: [
          {
            chave: "lugar",
            titulo: "Lugar",
            // Cabeçalho 0.15, célula 0.10: é a diferença que existe no código.
            larguraTitulo: 0.15,
            largura: 0.1,
            alinhamento: "direita",
            ativo: true,
          },
          {
            chave: "nomeProduto",
            titulo: "|       Produto",
            largura: 0.5,
            alinhamento: "centro",
            ativo: true,
          },
        ],
      },
      {
        tipo: "itens",
        titulo: "Itens — comanda",
        ativo: true,
        condicao: "comanda",
        campos: [],
        alinhamentoTabela: "centro",
        negritoCabecalho: false,
        // O ramo da comanda NÃO liga negrito na linha do produto hoje.
        negritoItem: false,
        ordenarPorLugar: true,
        separadorAntesDoCabecalho: regua("centro"),
        separadorDepoisDoCabecalho: regua("centro"),
        separadorFinal: regua("centro", { linhasEmBrancoDepois: 1 }),
        colunas: [
          {
            chave: "codigo",
            titulo: "Código",
            larguraTitulo: 0.15,
            largura: 0.1,
            alinhamento: "direita",
            ativo: true,
          },
          {
            chave: "nomeProduto",
            titulo: "|       Produto",
            largura: 0.5,
            alinhamento: "centro",
            ativo: true,
          },
        ],
      },
      {
        tipo: "itens",
        titulo: "Itens — delivery",
        ativo: true,
        condicao: "delivery",
        campos: [],
        alinhamentoTabela: "centro",
        negritoCabecalho: false,
        negritoItem: true,
        ordenarPorLugar: true,
        separadorAntesDoCabecalho: regua("centro"),
        separadorDepoisDoCabecalho: regua("centro"),
        separadorEntreItens: regua("centro"),
        separadorFinal: regua("centro", { linhasEmBrancoDepois: 1 }),
        colunas: [
          {
            chave: "quantidade",
            titulo: "Qtde",
            larguraTitulo: 0.15,
            largura: 0.1,
            alinhamento: "direita",
            ativo: true,
          },
          {
            chave: "nomeProduto",
            titulo: "Produto",
            largura: 0.5,
            alinhamento: "centro",
            ativo: true,
          },
        ],
      },
      {
        tipo: "observacoes",
        titulo: "Observações e combos",
        ativo: true,
        campos: [],
        prefixoObservacao: "> ",
        prefixoCombo: "* ",
        rotuloDescontoProduto: "Desconto",
        // A cozinha não vê preço: a via de produção não imprime nem o valor da
        // observação nem o desconto do produto.
        mostrarPrecoObservacao: false,
        mostrarDescontoProduto: false,
      },
      {
        // A via de produção não imprime valores. O bloco existe desligado para
        // aparecer no editor — ligar é uma decisão de quem opera.
        tipo: "totais",
        titulo: "Totais",
        ativo: false,
        campos: [
          campo("total", { rotulo: "Total", alinhamento: "direita" }),
        ],
      },
      {
        tipo: "rodape",
        titulo: "Rodapé",
        ativo: true,
        campos: [
          campo("outrasImpressoes", {
            rotulo: "Outras impressões",
            condicao: "temValor",
          }),
          regua("esquerda", {
            condicao: "temValor",
            chaveCondicao: "outrasImpressoes",
          }),
        ],
      },
    ],
  };
}

// ---------------------------------------------------------------------------
// VIA DE CONFERÊNCIA — a conta do cliente (printOrderConference)
// Largura 48 colunas, como está no código.
// ---------------------------------------------------------------------------

export function layoutPadraoConferencia(): LayoutComanda {
  return {
    versao: 1,
    nome: "Via de conferência (padrão do sistema)",
    documento: "conferencia",
    larguraColunas: 48,
    larguraPapelMm: 80,
    blocos: [
      {
        tipo: "cabecalho",
        titulo: "Cabeçalho",
        ativo: true,
        campos: [
          campo("filial", { alinhamento: "centro", negrito: true }),
          branco("centro"),
          campo("codigoPedido", {
            rotulo: "Número do Pedido",
            alinhamento: "centro",
            condicao: "mesaOuComanda",
          }),
          branco("centro"),
          campo("textoFixo", {
            texto: "VIA DE CONFERÊNCIA",
            alinhamento: "centro",
          }),
          branco("centro"),
        ],
      },
      {
        tipo: "dadosPedido",
        titulo: "Dados do pedido",
        ativo: true,
        campos: [
          campo("data", { rotulo: "Data" }),
          campo("periodo", { rotulo: "Período" }),
          campo("caixa", { rotulo: "Caixa" }),
          campo("operador", { rotulo: "Operador" }),
          campo("entregador", { rotulo: "Entregador", condicao: "temValor" }),
          branco("esquerda"),
          campo("identificacaoItem", {
            alinhamento: "centro",
            alturaDupla: true,
            larguraDupla: true,
          }),
          campo("lugarOuContaInteira", {
            alinhamento: "centro",
            alturaDupla: true,
            larguraDupla: true,
            condicao: "mesaOuComanda",
          }),
          branco("centro"),
          campo("clienteNome", {
            rotulo: "CLIENTE",
            maiusculas: true,
            condicao: "temValor",
          }),
          campo("clienteEndereco", {
            rotulo: "ENDEREÇO",
            maiusculas: true,
            condicao: "temValor",
          }),
          campo("clienteComplemento", {
            rotulo: "COMPLEMENTO",
            maiusculas: true,
            condicao: "temValor",
          }),
          campo("clienteObservacao", {
            rotulo: "OBSERVAÇÃO",
            maiusculas: true,
            condicao: "temValor",
          }),
          branco("centro"),
        ],
      },
      {
        tipo: "itens",
        titulo: "Itens",
        ativo: true,
        campos: [],
        alinhamentoTabela: "centro",
        negritoCabecalho: true,
        negritoItem: false,
        ordenarPorLugar: false,
        separadorAntesDoCabecalho: regua("centro", { negrito: true }),
        separadorFinal: regua("centro", { linhasEmBrancoDepois: 1 }),
        colunas: [
          {
            chave: "quantidade",
            titulo: "Qtde",
            largura: 0.1,
            alinhamento: "esquerda",
            ativo: true,
          },
          {
            chave: "nomeProduto",
            titulo: "Produto",
            largura: 0.5,
            alinhamento: "esquerda",
            alinhamentoTitulo: "centro",
            limiteCaracteres: 24,
            ativo: true,
          },
          {
            chave: "precoProduto",
            titulo: "Preço",
            largura: 0.2,
            alinhamento: "direita",
            ativo: true,
          },
        ],
      },
      {
        tipo: "observacoes",
        titulo: "Observações e combos",
        ativo: true,
        campos: [],
        prefixoObservacao: "> ",
        prefixoCombo: "* ",
        rotuloDescontoProduto: "Desconto",
        // Na conta do cliente o preço aparece.
        mostrarPrecoObservacao: true,
        mostrarDescontoProduto: true,
      },
      {
        tipo: "totais",
        titulo: "Totais",
        ativo: true,
        campos: [
          campo("totalProdutos", {
            rotulo: "Total Produtos",
            alinhamento: "direita",
            sufixo: "  ",
          }),
          campo("descontoConta", {
            rotulo: "Desconto Conta",
            alinhamento: "direita",
            sufixo: "  ",
            condicao: "diferenteDeZero",
          }),
          campo("descontoProdutos", {
            rotulo: "Desconto Produtos",
            alinhamento: "direita",
            sufixo: "  ",
            condicao: "diferenteDeZero",
          }),
          branco("direita"),
          campo("subtotal", {
            rotulo: "Subtotal",
            alinhamento: "direita",
            sufixo: "  ",
          }),
          branco("direita"),
          campo("taxaServico", {
            rotulo: "Serv. 10,00%",
            alinhamento: "direita",
            sufixo: "  ",
            condicao: "diferenteDeZero",
          }),
          campo("taxaEntrada", {
            rotulo: "Entrada",
            alinhamento: "direita",
            sufixo: "  ",
            condicao: "diferenteDeZero",
          }),
          campo("consumoMinimo", {
            rotulo: "Consumo Minimo",
            alinhamento: "direita",
            sufixo: "  ",
            condicao: "diferenteDeZero",
          }),
          campo("taxaEstacionamento", {
            rotulo: "Taxa de Estacionamento",
            alinhamento: "direita",
            sufixo: "  ",
            condicao: "diferenteDeZero",
          }),
          campo("taxaEntrega", {
            rotulo: "Taxa de Entrega",
            alinhamento: "direita",
            sufixo: "  ",
            condicao: "diferenteDeZero",
          }),
          regua("direita"),
          campo("total", {
            rotulo: "Total",
            alinhamento: "direita",
            sufixo: "  ",
            maiusculas: true,
            alturaDupla: true,
            larguraDupla: true,
          }),
          regua("direita", {
            condicao: "temValor",
            chaveCondicao: "pagamentos",
            linhasEmBrancoDepois: 1,
          }),
          campo("pagamentos", { alinhamento: "direita" }),
          campo("troco", {
            rotulo: "Troco",
            alinhamento: "direita",
            condicao: "diferenteDeZero",
          }),
        ],
      },
      {
        tipo: "rodape",
        titulo: "Rodapé",
        ativo: true,
        campos: [
          campo("permanencia", { rotulo: "Permanência", condicao: "mesa" }),
          campo("numeroPessoas", { rotulo: "Pessoas", condicao: "temValor" }),
          campo("totalPorPessoa", {
            rotulo: "Total por pessoa",
            condicao: "temValor",
          }),
          branco("esquerda"),
          regua("esquerda", { linhasEmBrancoDepois: 2 }),
          campo("textoFixo", { texto: "Assinatura:" }),
          regua("esquerda"),
          campo("textoFixo", { texto: "Este não é um cupom fiscal." }),
        ],
      },
    ],
  };
}
